import React from 'react';
import videojs from 'video.js';
import ReactPlayerLoader from '@brightcove/react-player-loader';
import VideoDetails from 'model/VideoDetails';
import ContentService from 'services/ContentService';
import AnalyticsService from 'services/AnalyticsService';
import AnalyticsData from 'model/AnalyticsData';
import { ContentType, EventType } from 'model/enums';

interface IBrightCovePlayerProps {
    accountId: string;
    controls: boolean;
    embed: string;
    playerId: string;
    videoId: string;
    onSuccess: (success: IOnSuccess) => void;
    onFailure: (error: string) => void;
}

export interface IOnSuccess {
    type: string;
    ref: videojs.Player | Element;
}

interface IState {
    percentViewed: number;
    percentViewedDecimal: number;
    totalLengthSecond: number;
    startPoint: number;
    endPoint: number;
    error: string;
}

export default class VideoPlayer extends React.Component<VideoDetails, IState> {
    private playerElem: React.ReactNode | null;
    private player?: videojs.Player;
    private playerProps: IBrightCovePlayerProps;

    constructor(props: VideoDetails) {
        super(props);

        this.state = {
            percentViewed: 0,
            percentViewedDecimal: 0,
            totalLengthSecond: 0,
            startPoint: 0,
            endPoint: 0,
            error: '',
        };
        this.playerElem = null;
        this.player = undefined;
        const { accountId, dataEmbed, playerId, brightcoveVideoId } = this.props;
        this.playerProps = {
            accountId: accountId,
            controls: true,
            embed: dataEmbed,
            playerId: playerId,
            videoId: brightcoveVideoId,
            onSuccess: this.onSuccess,
            onFailure: this.onFailure,
        };
    }

    componentDidMount() {
        this.playerElem = React.createElement(ReactPlayerLoader, this.playerProps);
        this.setState({});
        window.addEventListener('unload', this.saveProgress); // Save progress when patient close window or tab
    }

    componentWillUnmount() {
        window.removeEventListener('unload', this.saveProgress);
    }

    componentDidUpdate() {
        window.onpopstate = (e: any) => {
            // Save progress when patient press back button or clic link to back.
            this.saveProgress(e);
        };
    }

    onSuccess = (success: IOnSuccess) => {
        this.player = success.ref as videojs.Player;
        if (this.player) {
            this.player.fluid(true); // tells the player to auto size to container
            this.player.on('timeupdate', this.trackProgress);
            this.player.on('pause', this.onPause); // Save progress when patient pause the video.
            this.player.on('ended', this.onEnded); // Save progress when patient end video.
            this.player.on('firstplay', this.onStart);
            this.player.on('play', this.saveProgress); // Save progress when patient play the video.
            this.player.on('seeking', this.onSeeking);
        }
    };

    onFailure = (error: string) => {
        console.log(`VideoPlayer: failed to load, error ${error}`);
    };

    trackProgress = () => {
        if (this.player) {
            const endPoint = this.player.currentTime();
            const percentViewedDecimal = (endPoint / this.player.duration()) * 100;
            const percentViewed = Math.round(percentViewedDecimal);
            this.setState({
                percentViewed: percentViewed,
                percentViewedDecimal: percentViewedDecimal,
                endPoint: endPoint,
            });
        }
    };

    // Retrieve the video duration.
    onStart = async (e: any) => {
        if (this.player) {
            const totalLengthSecond = this.player.duration();
            this.setState({
                totalLengthSecond: totalLengthSecond,
            });
        }
    };

    // Send progress, analytics, and set new start point.
    onPause = async (e: any) => {
        this.saveProgress(e);
        this.sendAnalytics(e);
        this.setState({
            startPoint: this.state.endPoint,
        });
    };

    // Update start, end points, and calculate percent viewed.
    onSeeking = async (e: any) => {
        if (this.player) {
            const endPoint = this.player.currentTime();
            const percentViewedDecimal = (endPoint / this.player.duration()) * 100;
            this.setState({
                startPoint: endPoint,
                percentViewedDecimal: percentViewedDecimal,
            });
        }
    };

    onEnded = async (e: any) => {
        this.saveProgress(e);
        this.sendAnalytics(e);
    };

    sendAnalytics = async (e: any) => {
        e.preventDefault(); // Prevent Mozilla default behavior on close tab or window.
        e.returnValue = ''; // Prevent Chrome default behavior on close tab or window.
        delete e['returnValue']; // Guarantee the browser unload.
        let data: AnalyticsData = {
            contentType: ContentType.Video,
            eventType: EventType.Navigation,
            title: this.props.title,
            contentId: this.props.contentId,
            contentTypeId: this.props.contentTypeId,
            percentViewed: this.state.percentViewedDecimal,
            totalLengthSecond: this.state.totalLengthSecond,
            viewSegmentStart: this.state.startPoint,
            viewSegmentStop: this.state.endPoint,
        };
        AnalyticsService.recordEvent(data);
    };

    // Method to call API to save percent video viewed.
    saveProgress = async (e: any) => {
        e.preventDefault(); // Prevent Mozilla default behavior on close tab or window.
        e.returnValue = ''; // Prevent Chrome default behavior on close tab or window.
        delete e['returnValue']; // Guarantee the browser unload.
        const mediaKey = decodeURIComponent(this.props.mediaKey);
        try {
            await ContentService.postUpdateVideoPercentViewed(mediaKey, this.state.percentViewed);
        } catch (error) {
            this.setState({ error: error.message });
        }
    };

    render() {
        return this.playerElem;
    }
}
